// Cart service for API calls
import {
    CalendarCustomization,
    BusinessCardCustomization,
    CartItem,
    ItemPersonalization,
    Product,
    RawCartItem,
    RawLocationData,
    RawCustomization,
    ServiceResult,
    ProductVariation
} from '../types';
import {transformRawProduct} from './productService';
import {fetchWithErrorHandling, guestPrefix} from './apiUtils';
import {ApiError} from '../errors/ApiError';

// Known fields that get explicit mapping; everything else is spread through as-is
const KNOWN_LOCATION_FIELDS = new Set([
  'address', 'city', 'state', 'zip_code', 'agency_combine',
  'additional_agents', 'phone', 'cell_phone', 'fax', 'other_info',
]);

// Transform raw location data to our LocationData format
function transformLocationData(rawLocation: RawLocationData) {
  // Collect unknown extra fields from backend
  const extraFields: Record<string, string> = {};
  for (const key of Object.keys(rawLocation)) {
    if (!KNOWN_LOCATION_FIELDS.has(key)) {
      extraFields[key] = rawLocation[key];
    }
  }

  return {
    ...extraFields,
    address: rawLocation.address || '',
    city: rawLocation.city || '',
    state: rawLocation.state || '',
    zip_code: rawLocation.zip_code || '',
    agency_combine: rawLocation.agency_combine || '',
    additional_agents: rawLocation.additional_agents || '',
    phone: rawLocation.phone || '',
    cell_phone: rawLocation.cell_phone || '',
    fax: rawLocation.fax || '',
    other_info: rawLocation.other_info || '',
    textColor: '#000000',
    formScale: 0.5,
    formOpacity: 0.2,
  };
}

// Transform raw customization to BusinessCardCustomization
function transformBusinessCardCustomization(rawCustomization: RawCustomization | null): BusinessCardCustomization | undefined {
  if (!rawCustomization || !rawCustomization.fields) {
    return undefined;
  }

  return { ...rawCustomization.fields };
}

// Transform raw customization to CalendarCustomization
function transformCustomization(rawCustomization: RawCustomization | null): CalendarCustomization | undefined {
  if (!rawCustomization || !rawCustomization.fields) {
    return undefined;
  }

  const customization: CalendarCustomization = {
    locations: {
      primary: {
        ...transformLocationData(rawCustomization.fields),
      },
    },
  };

  if (rawCustomization.second_location) {
    customization.locations.secondary = {
      ...transformLocationData(rawCustomization.second_location),
    };
  }

  if (rawCustomization.third_location) {
    customization.locations.tertiary = {
      ...transformLocationData(rawCustomization.third_location),
    };
  }

  return customization;
}

// Transform raw cart item to CartItem
async function transformRawCartItem(rawItem: RawCartItem): Promise<CartItem | null> {
  const fullProduct = rawItem.product ? transformRawProduct(rawItem.product) : null;

  // Construct a minimal Product object from cart data
  // The backend provides name, image, and price already
  const product: Product = fullProduct || {
    id: rawItem.productId,
    name: rawItem.name,
    description: '',
    basePrice: rawItem.price,
    images: [rawItem.image],
    variations: [],
    isPrintDetail: rawItem.isPrintDetail,
    allowBackorder: false,
    allowOnDemand: false,
    departments: [],
    categories: [],
    productionTimeline: rawItem.productionTimeline,
  };

  // Find matching variation from full product data if available
  let variation: ProductVariation;

  if (fullProduct && fullProduct.variations && fullProduct.variations.length > 0) {
    // Try to find exact match by color and size
    let matchedVariation = fullProduct.variations.find(v => {
      const colorMatch = !rawItem.color || v.color === rawItem.color;
      const sizeMatch = !rawItem.size || v.size === rawItem.size;
      return colorMatch && sizeMatch && v.color === rawItem.color && v.size === rawItem.size;
    });

    // If no exact match, try color-only match
    if (!matchedVariation && rawItem.color && !rawItem.size) {
      matchedVariation = fullProduct.variations.find(v => v.color === rawItem.color);
    }

    // If no exact match, try size-only match
    if (!matchedVariation && rawItem.size && !rawItem.color) {
      matchedVariation = fullProduct.variations.find(v => v.size === rawItem.size);
    }

    // If still no match, use first variation
    if (!matchedVariation) {
      matchedVariation = fullProduct.variations[0];
    }

    variation = { ...matchedVariation, price: rawItem.price };
  } else {
    // Construct a minimal ProductVariation object when no product data available
    variation = {
      id: `${rawItem.productId}-${rawItem.color || 'default'}-${rawItem.size || 'default'}`,
      color: rawItem.color || 'Default',
      size: rawItem.size || 'Standard',
      price: rawItem.price,
      stock: 999, // Unknown from cart data, assume available
      images: [rawItem.image],
    };
  }

  // Transform customization based on product print type
  const printDetailType = fullProduct?.printDetailType;
  const calendarCustomization = printDetailType === 'calendar'
    ? transformCustomization(rawItem.customization)
    : undefined;
  const businessCardCustomization = printDetailType === 'business_card'
    ? transformBusinessCardCustomization(rawItem.customization)
    : undefined;
  const customizeItemCustomization = printDetailType === 'customize_item'
    ? transformBusinessCardCustomization(rawItem.customization)
    : undefined;

  // Transform personalizations array if present
  const personalizations: ItemPersonalization[] | undefined =
    rawItem.personalizations?.length
      ? rawItem.personalizations.map(p => ({
          text: p.text,
          placement: p.placement,
          fee: rawItem.personalization_fee ?? undefined,
        }))
      : undefined;

  return {
    id: rawItem.id,
    shoppingCartId: rawItem.shoppingCartId,
    productId: rawItem.productId,
    variationId: String(variation.id),
    quantity: rawItem.quantity,
    product,
    variation,
    calendarCustomization,
    businessCardCustomization,
    customizeItemCustomization,
    personalizations,
    logoOptionName: rawItem.logoOptionName ?? null,
    secondLogoOptionId: rawItem.secondLogoOptionId ?? null,
    secondLogoOptionName: rawItem.secondLogoOptionName ?? null,
    secondLogoFee: rawItem.secondLogoFee ?? null,
    customerCharges: rawItem.customerCharges ?? [],
  };
}

export class CartService {
  static async getCart(): Promise<CartItem[]> {
    const endpoint = `/api/v2${guestPrefix()}/cart/items`;
    const rawCartItems = await fetchWithErrorHandling<RawCartItem[]>(endpoint);

    if (rawCartItems.length === 0) return [];

    const cartItems = await Promise.all(
      rawCartItems.map(rawItem => transformRawCartItem(rawItem))
    );

    return cartItems.filter((item): item is CartItem => item !== null);
  }

  static async addToCart(
    productId: number,
    variationId: string,
    quantity: number,
    customization?: CalendarCustomization,
    color?: string,
    size?: string,
    personalizations?: ItemPersonalization[],
    businessCardCustomization?: BusinessCardCustomization,
    logoOptionId?: number | null,
    secondLogoOptionId?: number | null,
    customizeItemCustomization?: BusinessCardCustomization,
  ): Promise<ServiceResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/cart/items`;

      await fetchWithErrorHandling(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          product_id: productId,
          variation_id: variationId,
          quantity,
          color,
          size,
          customization: customization
            ? JSON.stringify(this.transformCustomizationToAPI(customization))
            : businessCardCustomization
              ? JSON.stringify(this.transformBusinessCardCustomizationToAPI(businessCardCustomization))
              : customizeItemCustomization
                ? JSON.stringify(this.transformBusinessCardCustomizationToAPI(customizeItemCustomization))
                : null,
          personalizations: personalizations?.length
            ? personalizations.map(p => ({ text: p.text, placement: p.placement }))
            : null,
          logoOptionId: logoOptionId ?? null,
          secondLogoOptionId: secondLogoOptionId ?? null,
        }),
      }, { handle422Errors: true });

      return { success: true };
    } catch (error: unknown) {
      // Handle Laravel 422 validation errors
      if (error instanceof ApiError && error.statusCode === 422) {
        return { success: false, warningMessage: error.message };
      }

      console.error('Failed to add item to cart:', error);
      throw new Error('Unable to add item to cart. Please try again later.');
    }
  }

  static async updateCartItemQuantity(itemId: string, quantity: number): Promise<ServiceResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/cart/items/${itemId}/quantity`;

      await fetchWithErrorHandling(endpoint, {
        method: 'POST',
        body: JSON.stringify({ quantity }),
      }, { handle422Errors: true });

      return { success: true };
    } catch (error: unknown) {
      // Handle Laravel 422 validation errors
      if (error instanceof ApiError && error.statusCode === 422) {
        return { success: false, warningMessage: error.message };
      }

      console.error('Failed to update cart item quantity:', error);
      throw new Error('Unable to update cart item quantity. Please try again later.');
    }
  }

  static async updateCartItemCustomization(
    itemId: string,
    customization?: CalendarCustomization,
    businessCardCustomization?: BusinessCardCustomization,
    customizeItemCustomization?: BusinessCardCustomization,
  ): Promise<ServiceResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/cart/items/${itemId}/customization`;

      await fetchWithErrorHandling(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          customization: customization
            ? JSON.stringify(this.transformCustomizationToAPI(customization))
            : businessCardCustomization
              ? JSON.stringify(this.transformBusinessCardCustomizationToAPI(businessCardCustomization))
              : customizeItemCustomization
                ? JSON.stringify(this.transformBusinessCardCustomizationToAPI(customizeItemCustomization))
                : null,
        }),
      }, { handle422Errors: true });

      return { success: true };
    } catch (error: unknown) {
      if (error instanceof ApiError && error.statusCode === 422) {
        return { success: false, warningMessage: error.message };
      }

      console.error('Failed to update cart item customization:', error);
      throw new Error('Unable to update cart item customization. Please try again later.');
    }
  }

  static async removeFromCart(itemId: string): Promise<ServiceResult> {
    try {
      const endpoint = `/api/v2${guestPrefix()}/cart/items/${itemId}`;

      await fetchWithErrorHandling(endpoint, {
        method: 'DELETE',
      }, { handle422Errors: true });

      return { success: true };
    } catch (error: unknown) {
      // Handle Laravel 422 validation errors
      if (error instanceof ApiError && error.statusCode === 422) {
        return { success: false, warningMessage: error.message };
      }

      console.error('Failed to remove item from cart:', error);
      throw new Error('Unable to remove item from cart. Please try again later.');
    }
  }

    static async clearCart(): Promise<ServiceResult> {
        try {
            const endpoint = `/api/v2${guestPrefix()}/cart`;

            await fetchWithErrorHandling(endpoint, {
                method: 'DELETE',
            });

            return {success: true};
        } catch (error) {
            console.error('Failed to clear cart:', error);
            throw new Error('Unable to clear cart. Please try again later.');
        }
    }


  // Extract a RawLocationData from a LocationData, preserving extra fields
  private static locationToRaw(loc: CalendarCustomization['locations']['primary']): RawLocationData {
    // Collect extra fields (not in the known set, not internal-only fields)
    const internalOnly = new Set(['calendarImage', 'fontFamily', 'textColor', 'formScale', 'formOpacity', 'formPosition']);
    const extraFields: Record<string, string> = {};
    for (const key of Object.keys(loc)) {
      if (!KNOWN_LOCATION_FIELDS.has(key) && !internalOnly.has(key)) {
        const val = (loc as Record<string, unknown>)[key];
        if (typeof val === 'string') extraFields[key] = val;
      }
    }
    return {
      ...extraFields,
      address: loc.address,
      city: loc.city,
      state: loc.state,
      zip_code: loc.zip_code,
      agency_combine: loc.agency_combine,
      additional_agents: loc.additional_agents,
      phone: loc.phone,
      cell_phone: loc.cell_phone,
      fax: loc.fax || '',
      other_info: loc.other_info,
    };
  }

    // Helper method to transform CalendarCustomization back to API format
    private static transformCustomizationToAPI(customization: CalendarCustomization): RawCustomization {
        const result: RawCustomization = {
            fields: CartService.locationToRaw(customization.locations.primary),
        };

    if (customization.locations.secondary) {
      result.second_location = CartService.locationToRaw(customization.locations.secondary);
    }

    if (customization.locations.tertiary) {
      result.third_location = CartService.locationToRaw(customization.locations.tertiary);
    }

    return result;
  }

  private static transformBusinessCardCustomizationToAPI(customization: BusinessCardCustomization): object {
    return { fields: { ...customization } };
  }
}
